import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiBookOpen, FiSearch, FiClock } from "react-icons/fi";
import { API_BASE } from "../../../api";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsLMS";
import { getSessionUserId } from "../../../utils/SessionUser";
import { carreraNormalize } from "../Objetos/CarreraModel";
import AlertDialog from "../Components/Shared/AlertDialog";
import ConfirmDialog from "../Components/Shared/ConfirmDialog";

const CarrerasMarketplace = () => {
    const navigate = useNavigate();
    const [carreras, setCarreras] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inscripciones, setInscripciones] = useState([]); // CarreraEstudiante existentes
    const [idEstudiante, setIdEstudiante] = useState(null);
    const [registeringId, setRegisteringId] = useState(null);
    const [isAdminLMS, setIsAdminLMS] = useState(false); // Si el usuario es Administrador LMS
    const [isEstudiante, setIsEstudiante] = useState(false); // Si el usuario es Estudiante
    const [vistaAdmin, setVistaAdmin] = useState('todos'); // 'revisar' o 'todos'
    const [validationsComplete, setValidationsComplete] = useState(false);
    const [revisionCount, setRevisionCount] = useState(0);

    // Filtros visuales
    const [searchTerm, setSearchTerm] = useState("");

    // Estados para modales
    const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

    const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");

    // 1. Cargar idEstudiante y verificar roles
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const idUsuario = getSessionUserId();
                if (!idUsuario) {
                    setValidationsComplete(true);
                    return;
                }

                const [resUsuarioRoles, resEstudiante] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.usuarioRolByUsuario(idUsuario)),
                    axios.get(LMS_ENDPOINTS.estudianteByUsuario(idUsuario)).catch(() => ({ data: null })),
                ]);

                const usuarioRoles = resUsuarioRoles.data || [];
                const estudianteRecord = resEstudiante.data ? Models.estudiante(resEstudiante.data) : null;

                const tieneRolAdminLMS = usuarioRoles.some(ur => Number(ur.idRol || ur.IdRol) === 1008);
                const tieneRolEstudiante = usuarioRoles.some(ur => Number(ur.idRol || ur.IdRol) === 3);

                setIsAdminLMS(tieneRolAdminLMS);
                setIsEstudiante(tieneRolEstudiante);

                if (tieneRolAdminLMS && !tieneRolEstudiante) {
                    setVistaAdmin('revisar');
                } else {
                    setVistaAdmin('todos');
                }

                if (estudianteRecord) {
                    setIdEstudiante(estudianteRecord.idEstudiante ?? estudianteRecord.IdEstudiante);
                }

                setValidationsComplete(true);
            } catch (error) {
                console.error('[CarrerasMarketplace] Error en validación inicial:', error);
                setValidationsComplete(true);
            }
        };

        loadInitialData();
    }, []);

    // 2. Debounce para la búsqueda (600ms)
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(searchTerm), 600);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // 3. Cargar Carreras e Inscripciones
    useEffect(() => {
        if (!validationsComplete) return;

        const loadContent = async () => {
            setLoading(true);
            try {
                const idUsuario = getSessionUserId();
                let respCarreras;

                if (vistaAdmin === 'revisar') {
                    respCarreras = await axios.get(LMS_ENDPOINTS.carrerasPendientesRevision(idUsuario));
                } else {
                    respCarreras = await axios.get(LMS_ENDPOINTS.carrerasMarketplace(idUsuario));
                }

                // Cargar conteo de revisiones si es admin
                if (isAdminLMS) {
                    const resPend = await axios.get(LMS_ENDPOINTS.carrerasPendientesRevision(idUsuario)).catch(() => ({ data: [] }));
                    setRevisionCount((resPend.data || []).length);
                }

                const rawCarreras = respCarreras.data || [];
                setCarreras(rawCarreras.map(c => carreraNormalize(c)));
            } catch (error) {
                console.error("[CarrerasMarketplace] Error cargando contenido:", error);
            } finally {
                setLoading(false);
            }
        };

        loadContent();
    }, [validationsComplete, vistaAdmin, idEstudiante, isAdminLMS]);

    // handlers
    const handleRegister = async (carrera) => {
        if (!idEstudiante) {
            setAlertDialog({
                open: true,
                title: 'Error',
                message: 'No se pudo identificar al estudiante actual. No es posible registrar la carrera.',
                variant: 'error'
            });
            return;
        }

        const idCarrera = carrera.idCarrera || carrera.id || carrera.IdCarrera;
        if (!idCarrera) {
            setAlertDialog({ open: true, title: 'Error', message: 'ID de carrera no válido.', variant: 'error' });
            return;
        }

        setConfirmDialog({
            open: true,
            title: 'Confirmar inscripción',
            message: `¿Deseas registrarte en la carrera "${carrera.nombre}"?`,
            onConfirm: () => proceedWithRegistration(carrera, idCarrera)
        });
    };

    const proceedWithRegistration = async (carrera, idCarrera) => {
        try {
            setRegisteringId(idCarrera);

            const dto = {
                IdCarreraEstudiante: 0,
                IdEstudiante: Number(idEstudiante),
                IdCarrera: Number(idCarrera),
                Activo: true,
                FechaInscripcion: new Date().toISOString()
            };

            await axios.post(LMS_ENDPOINTS.carreraEstudiante, dto);

            setCarreras(prev => prev.filter(c => Number(c.idCarrera || c.id) !== Number(idCarrera)));

            setAlertDialog({ open: true, title: '¡Éxito!', message: 'Te has registrado correctamente en la carrera.', variant: 'success' });
        } catch (error) {
            console.error("Error registration:", error);
            setAlertDialog({ open: true, title: 'Error', message: 'Ocurrió un error al registrar la carrera.', variant: 'error' });
        } finally {
            setRegisteringId(null);
        }
    };

    return (
        <div className="panel-sigma-main">
            <div className="profile-header-title">
                <h1>Oferta de Carreras</h1>
            </div>

            {/* UI de Toggle */}
            <div style={{ marginBottom: '2rem', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.05))' }}>
                <div style={{
                    display: 'flex',
                    width: '100%',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '12px 12px 0 0',
                    overflow: 'hidden',
                    padding: '0.4rem',
                    gap: '0.5rem',
                    border: '1px solid #e2e8f0',
                    borderBottom: 'none'
                }}>
                    {(isAdminLMS && isEstudiante) ? (
                        <>
                            <div
                                onClick={() => setVistaAdmin('revisar')}
                                style={{
                                    flex: 1,
                                    textAlign: 'center',
                                    padding: '0.4rem 0',
                                    fontSize: '0.9rem',
                                    fontWeight: vistaAdmin === 'revisar' ? 700 : 500,
                                    color: vistaAdmin === 'revisar' ? 'var(--color-primary-dark)' : '#64748b',
                                    backgroundColor: vistaAdmin === 'revisar' ? '#ffffff' : 'transparent',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    userSelect: 'none',
                                    boxShadow: vistaAdmin === 'revisar' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                                }}
                            >
                                📋 Revisión ({revisionCount})
                            </div>
                            <div
                                onClick={() => setVistaAdmin('todos')}
                                style={{
                                    flex: 1,
                                    textAlign: 'center',
                                    padding: '0.4rem 0',
                                    fontSize: '0.9rem',
                                    fontWeight: vistaAdmin === 'todos' ? 700 : 500,
                                    color: vistaAdmin === 'todos' ? 'var(--color-primary-dark)' : '#64748b',
                                    backgroundColor: vistaAdmin === 'todos' ? '#ffffff' : 'transparent',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    userSelect: 'none',
                                    boxShadow: vistaAdmin === 'todos' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                                }}
                            >
                                📚 Catálogo completo
                            </div>
                        </>
                    ) : (
                        <div
                            style={{
                                flex: 1,
                                textAlign: 'center',
                                padding: '0.4rem 0',
                                fontSize: '0.9rem',
                                fontWeight: 700,
                                color: 'var(--color-primary-dark)',
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                userSelect: 'none',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                        >
                            {isAdminLMS ? `📋 Revisión (${revisionCount})` : "📚 Catálogo de Carreras"}
                        </div>
                    )}
                </div>

                <div style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    padding: '0.8rem 1rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{
                        flex: 1,
                        minWidth: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '999px',
                        padding: '0.4rem 1rem',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                        <FiSearch style={{ color: 'var(--color-primary-dark)', marginRight: '0.6rem', fontSize: '1rem' }} />
                        <input
                            type="text"
                            className="lms-search-input"
                            placeholder="Buscar carreras..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: '0.9rem', color: '#1e293b' }}
                        />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="catalog-loading">Cargando carreras...</div>
            ) : (
                <div className="courses-grid fixed-cards">
                    {(() => {
                        const visibles = (!carreras ? [] : carreras).filter((carrera) => {
                            if (vistaAdmin === 'revisar') return true;

                            if (debouncedSearch) {
                                const name = (carrera.nombre || carrera.Nombre || '').toLowerCase();
                                const desc = (carrera.descripcion || carrera.Descripcion || '').toLowerCase();
                                const search = debouncedSearch.toLowerCase();
                                if (!name.includes(search) && !desc.includes(search)) return false;
                            }

                            return true;
                        });

                        if (visibles.length === 0) {
                            return (
                                <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "3rem", color: "#94a3b8" }}>
                                    No hay carreras disponibles en este momento.
                                </div>
                            );
                        }

                        return visibles.map((carrera, index) => {
                            const imagePath = carrera.urlImagen || carrera.UrlImagen || "";
                            const hasImage = !!imagePath;
                            const imageUrl = imagePath.startsWith('http') ? imagePath : `${SERVER_URL}${imagePath}`;

                            const cardVisuals = (() => {
                                if (vistaAdmin === 'revisar') {
                                    return {
                                        color: '#eab308', // Yellow
                                        icon: <FiClock />
                                    };
                                } else {
                                    if (carrera.compartido) {
                                        return {
                                            color: '#3b82f6', // Blue
                                            icon: <FiBookOpen />
                                        };
                                    } else {
                                        return {
                                            color: '#10b981', // Green
                                            icon: <FiBookOpen />
                                        };
                                    }
                                }
                            })();

                            return (
                                <article
                                    key={carrera.idCarrera || index}
                                    className="course-card fixed-card"
                                    style={{
                                        border: `1px solid ${cardVisuals.color}`,
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <div className="course-thumb" style={{ padding: 0, position: 'relative' }}>
                                        {hasImage ? (
                                            <img src={imageUrl} alt={carrera.nombre} style={{ width: "100%", height: "100%", objectFit: "fill", display: "block" }} />
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e2e8f0', color: '#94a3b8', height: '100%' }}>
                                                <FiBookOpen size={48} />
                                            </div>
                                        )}

                                        {/* Triángulo de estatus en la esquina superior derecha */}
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            width: '60px',
                                            height: '60px',
                                            zIndex: 5,
                                            overflow: 'hidden'
                                        }}>
                                            {/* El Triángulo */}
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                width: 0,
                                                height: 0,
                                                borderStyle: 'solid',
                                                borderWidth: '0 60px 60px 0',
                                                borderColor: `transparent ${cardVisuals.color} transparent transparent`,
                                                filter: 'drop-shadow(-1px 1px 2px rgba(0,0,0,0.2))'
                                            }} />

                                            {/* El Icono */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '8px',
                                                right: '8px',
                                                color: 'white',
                                                fontSize: '1.5rem',
                                                display: 'flex',
                                                zIndex: 6
                                            }}>
                                                {cardVisuals.icon}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="course-body" style={{
                                        backgroundColor: vistaAdmin === 'revisar'
                                            ? '#fffbeb'
                                            : carrera.compartido
                                                ? '#eff6ff'
                                                : '#f0fdf4',
                                        flex: 1,
                                        transition: 'background-color 0.3s ease',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        <h3 className="course-title">{carrera.nombre}</h3>
                                        <div style={{ marginTop: "0.8rem", flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            {/* Badge de Empresa / Procedencia */}
                                            <div style={{
                                                fontSize: '0.75rem',
                                                backgroundColor: carrera.compartido ? '#f5f3ff' : '#eff6ff',
                                                color: carrera.compartido ? '#7c3aed' : '#2563eb',
                                                padding: '2px 10px',
                                                borderRadius: '9999px',
                                                fontWeight: 600,
                                                width: 'fit-content',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                border: `1px solid ${carrera.compartido ? '#ddd6fe' : '#bfdbfe'}`,
                                                marginBottom: '0.8rem'
                                            }}>
                                                {carrera.compartido ? '🌍 Compartido por: ' : '🏢 '}
                                                {carrera.empresaNombre || 'General'}
                                            </div>
                                            <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.5, marginBottom: '1rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {carrera.descripcion || "Sin descripción disponible."}
                                            </p>
                                            <p style={{ fontSize: '0.8rem', color: '#475569', fontWeight: 600 }}>
                                                Instructor: {carrera.instructorNombreCompleto || 'Sin instructor'}
                                            </p>
                                        </div>
                                        <div className="course-actions">
                                            <button
                                                type="button"
                                                className="btn-action"
                                                style={{ width: '100%', padding: '0.5rem 1rem', cursor: 'pointer', transition: '0.2s', fontFamily: "'Outfit', sans-serif" }}
                                                onClick={() => {
                                                    if (vistaAdmin === 'revisar') {
                                                        navigate(`/menu/lms/carrera-curriculo-revision/${carrera.idCarrera || carrera.IdCarrera || carrera.id}`);
                                                    } else {
                                                        handleRegister(carrera);
                                                    }
                                                }}
                                                disabled={registeringId === (carrera.idCarrera || carrera.id)}
                                            >
                                                {vistaAdmin === 'revisar' ? "Revisar" : (registeringId === (carrera.idCarrera || carrera.id) ? "Registrando..." : "Registrarte")}
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            );
                        });
                    })()}
                </div>
            )}

            <AlertDialog open={alertDialog.open} onClose={() => setAlertDialog({ ...alertDialog, open: false })} title={alertDialog.title} message={alertDialog.message} variant={alertDialog.variant} />
            <ConfirmDialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })} onConfirm={confirmDialog.onConfirm} title={confirmDialog.title} message={confirmDialog.message} />
        </div>
    );
};

export default CarrerasMarketplace;
